import React, { useState, useEffect } from 'react';
import {
    Typography,
    Box,
    Paper,
    Divider,
    FormControl,
    Select,
    MenuItem,
    Radio,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    IconButton
} from '@mui/material';
import InfoRoundedIcon from '@mui/icons-material/InfoOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PaletaColores from '../../components/PaletaColores';
import PlataformaSelector from '../../components/guias/PlataformaSelector';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import Swal from 'sweetalert2';
import ActiveCampaignForm from '../../components/ActiveCampaignForm';

export const availableIcons = {
    whiteRoundIcon: "/img/whiteRoundIcon.svg",
    whiteSquareIcon: "/img/whiteSquareIcon.svg",
    whiteThinIcon: "/img/whiteThinIcon.svg",
    blackRoundIcon: "/img/blackRoundIcon.svg",
    blackSquareIcon: "/img/blackSquareIcon.svg",
    blackThinIcon: "/img/blackThinIcon.svg",
    sol: "/img/lightMode.svg",
    luna: "/img/darkMode.svg"
};

const positionStyles = {
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
};

const positions = [
    { name: 'top-left', label: 'Arriba-Izquierda', top: '33%', left: '5%' },
    { name: 'middle-left', label: 'Centro-Izquierda', top: '62%', left: '5%' },
    { name: 'bottom-left', label: 'Abajo-Izquierda', top: '90%', left: '5%' },
    { name: 'top-center', label: 'Arriba-Centro', top: '33%', left: '50%' },
    { name: 'bottom-center', label: 'Abajo-Centro', top: '90%', left: '50%' },
    { name: 'top-right', label: 'Arriba-Derecha', top: '33%', left: '95%' },
    { name: 'middle-right', label: 'Centro-Derecha', top: '62%', left: '95%' },
    { name: 'bottom-right', label: 'Abajo-Derecha', top: '90%', left: '95%' },
];

const labelToName = Object.fromEntries(positions.map(p => [p.label, p.name]));
const nameToLabel = Object.fromEntries(positions.map(p => [p.name, p.label]));

// Helpers para mapear los valores seleccionados a los del script
const modeMap = { Claro: "light", Oscuro: "dark" };
const iconPositionMap = {
    "top-left": "topLeft",
    "middle-left": "middleLeft",
    "bottom-left": "bottomLeft",
    "top-center": "topCenter",
    "bottom-center": "bottomCenter",
    "top-right": "topRight",
    "middle-right": "middleRight",
    "bottom-right": "bottomRight"
};
const autoDeployMap = { Abierto: true, Cerrado: false };
const windowPositionMap = { Derecha: "right", Izquierda: "left" };

// Mapear el icono seleccionado a su clave
const iconTypeMap = {
    "whiteRoundIcon": "whiteRoundIcon",
    "whiteSquareIcon": "whiteSquareIcon",
    "whiteThinIcon": "whiteThinIcon",
    "blackRoundIcon": "blackRoundIcon",
    "blackSquareIcon": "blackSquareIcon",
    "blackThinIcon": "blackThinIcon",
    "accessibilidad-white": "accessibilityWhite",
    "accessibilidad-black": "accessibilityBlack"
};

// Replace the SelectorPosicionador component with responsive dimensions
function SelectorPosicionador({ selectedPosition, setSelectedPosition }) {
    const handleSelectChange = (event) => {
        setSelectedPosition(labelToName[event.target.value]);
    };

    return (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
            {/* Make the positioner responsive */}
            <Box sx={{ 
                width: '100%',
                maxWidth: { xs: '100%', md: 400 },
                height: 175, 
                bgcolor: 'white', 
                borderRadius: 2, 
                boxShadow: 3, 
                position: 'relative' 
            }}>
                {/* Header */}
                <Box
                    sx={{
                        height: 40,
                        bgcolor: '#c7c7c7',
                        borderTopLeftRadius: 9,
                        borderTopRightRadius: 9,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ width: 12, height: 12, backgroundColor: 'white', borderRadius: '50%', ml: { xs: 1.2, md: 1.8 } }} />
                    <Box
                        sx={{
                            height: 12,
                            width: '80%',
                            borderRadius: 8,
                            backgroundColor: 'white',
                            opacity: 0.9,
                            margin: '0 auto',
                        }}
                    />
                    <Box sx={{ width: 12, height: 12, backgroundColor: 'white', borderRadius: '50%', mr: { xs: 1.2, md: 1.8 } }} />
                </Box>

                {/* Position buttons */}
                {positions.map(({ name, top, left }) => (
                    <Box key={name} sx={{ ...positionStyles, top, left }}>
                        <Radio
                            checked={selectedPosition === name}
                            onChange={() => setSelectedPosition(name)}
                            value={name}
                            color="primary"
                            sx={{ transform: 'scale(1.3)' }}
                        />
                    </Box>
                ))}
            </Box>

            {/* Selector below */}
            <FormControl fullWidth sx={{ width: '100%', mt: 2 }}>
                <Select
                    id="position-selector"
                    value={nameToLabel[selectedPosition]}
                    onChange={handleSelectChange}
                    displayEmpty
                    sx={{ fontSize: 15.5, height: 50 }}
                >
                    {positions.map(({ label }) => (
                        <MenuItem key={label} value={label}>
                            {label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
}

const tamanosBoton = {
    Pequeño: {
        width: '30px',
        height: '30px',
        fontSize: '14px',
    },
    Mediano: {
        width: '52px',
        height: '52px',
        fontSize: '16px',
    },
    Grande: {
        width: '80px',
        height: '80px',
        fontSize: '18px',
    },
};

const tamanosCuadrados = {
    Pequeño: {
        width: '116px',
        height: '30px',
        fontSize: '11px',
    },
    Mediano: {
        width: '140px',
        height: '40px',
        fontSize: '14.1px',
    },
    Grande: {
        width: '160px',
        height: '50px',
        fontSize: '17px',
    },
};

// 1. Definir posiciones para cada combinación de posición y tamaño
const positions2 = {
    'top-left': {
        'Pequeño': { top: '17%', left: '5%' },
        'Mediano': { top: '20%', left: '7%' },
        'Grande': { top: '23%', left: '9%' }
    },
    'middle-left': {
        'Pequeño': { top: '52%', left: '5%' },
        'Mediano': { top: '51%', left: '7%' },
        'Grande': { top: '50%', left: '9%' }
    },
    'bottom-left': {
        'Pequeño': { top: '93%', left: '5%' },
        'Mediano': { top: '90%', left: '7%' },
        'Grande': { top: '87%', left: '9%' }
    },
    'top-center': {
        'Pequeño': { top: '17%', left: '51%' },
        'Mediano': { top: '20%', left: '51%' },
        'Grande': { top: '23%', left: '51%' }
    },
    'bottom-center': {
        'Pequeño': { top: '94%', left: '51%' },
        'Mediano': { top: '91%', left: '51%' },
        'Grande': { top: '88%', left: '51%' }
    },
    'top-right': {
        'Pequeño': { top: '17%', left: '95%' },
        'Mediano': { top: '20%', left: '93%' },
        'Grande': { top: '23%', left: '91%' }
    },
    'middle-right': {
        'Pequeño': { top: '52%', left: '95%' },
        'Mediano': { top: '51%', left: '93%' },
        'Grande': { top: '50%', left: '91%' }
    },
    'bottom-right': {
        'Pequeño': { top: '93%', left: '95%' },
        'Mediano': { top: '90%', left: '93%' },
        'Grande': { top: '87%', left: '91%' }
    }
};

const positionsCuadrados = {
    'top-left': {
        'Pequeño': { top: '15%', left: '11%' },
        'Mediano': { top: '16%', left: '13%' },
        'Grande': { top: '17%', left: '15%' }
    },
    'middle-left': {
        'Pequeño': { top: '52%', left: '11%' },
        'Mediano': { top: '51%', left: '13%' },
        'Grande': { top: '50%', left: '15%' }
    },
    'bottom-left': {
        'Pequeño': { top: '94%', left: '11%' },
        'Mediano': { top: '93%', left: '13%' },
        'Grande': { top: '92%', left: '15%' }
    },
    'top-center': {
        'Pequeño': { top: '15%', left: '51%' },
        'Mediano': { top: '16%', left: '51%' },
        'Grande': { top: '17%', left: '51%' }
    },
    'bottom-center': {
        'Pequeño': { top: '94%', left: '51%' },
        'Mediano': { top: '93%', left: '51%' },
        'Grande': { top: '92%', left: '51%' }
    },
    'top-right': {
        'Pequeño': { top: '15%', left: '89%' },
        'Mediano': { top: '16%', left: '87%' },
        'Grande': { top: '17%', left: '85%' }
    },
    'middle-right': {
        'Pequeño': { top: '52%', left: '89%' },
        'Mediano': { top: '51%', left: '87%' },
        'Grande': { top: '50%', left: '85%' }
    },
    'bottom-right': {
        'Pequeño': { top: '94%', left: '89%' },
        'Mediano': { top: '93%', left: '87%' },
        'Grande': { top: '92%', left: '85%' }
    }
};

const PersonalizarWidget = () => {
    const { t } = useTranslation();
    // Obtener los valores pasados por state desde ModificarPlan
    const location = useLocation();
    const {
        domain = '',
        autoDeploy,
        color,
        defaultLanguage,
        iconPosition,
        iconSizeValue,
        iconType,
        mode,
        windowPosition
    } = location.state || {};

    // Inicializar los estados con los valores recibidos por state (o valores por defecto)
    const [colorPrincipal, setColorPrincipal] = useState(color || '#0f50d2');
    const [selectedIcon, setSelectedIcon] = useState(() => {
        if (iconType) {
            // iconType es la key que espera el backend/script
            // Si es accessibilidad, ya está bien
            if (iconType === "accessibilityWhite") return "accessibilidad-white";
            if (iconType === "accessibilityBlack") return "accessibilidad-black";
            // Para los demás, usa la key directamente
            if (Object.values(iconTypeMap).includes(iconType)) {
                // Busca la key de iconTypeMap que tiene ese value
                const foundKey = Object.keys(iconTypeMap).find(k => iconTypeMap[k] === iconType);
                return foundKey || "whiteRoundIcon";
            }
        }
        return "whiteRoundIcon";
    });
    const [valor, setValor] = useState(iconSizeValue || 'Mediano');
    const [valor2, setValor2] = useState(
        typeof autoDeploy === 'boolean' ? (autoDeploy ? 'Abierto' : 'Cerrado') : 'Cerrado'
    );
    const [valor3, setValor3] = useState(mode === 'dark' ? 'Oscuro' : 'Claro');
    const [valor4, setValor4] = useState(windowPosition === 'left' ? 'Izquierda' : 'Derecha');
    const [valor5, setValor5] = useState(defaultLanguage || 'es');
    const [selectedPosition, setSelectedPosition] = useState(() => {
        if (iconPosition) {
            const posKey = Object.keys(iconPositionMap).find(
                key => iconPositionMap[key] === iconPosition
            );
            if (posKey) return posKey;
        }
        return 'bottom-right';
    });

    // Handlers para los Select
    const handleChange = (event) => setValor(event.target.value);
    const handleChange2 = (event) => setValor2(event.target.value);
    const handleChange3 = (event) => setValor3(event.target.value);
    const handleChange4 = (event) => setValor4(event.target.value);
    const handleChange5 = (event) => setValor5(event.target.value);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // 2. Obtener la posición según la combinación de posición y tamaño y el tipo de icono
    let selectedPositionData;
    if (selectedIcon === 'accessibilidad-white' || selectedIcon === 'accessibilidad-black') {
        selectedPositionData = positionsCuadrados[selectedPosition]?.[valor];
    } else {
        selectedPositionData = positions2[selectedPosition]?.[valor];
    }

    // Evitar error si selectedPositionData es undefined
    if (!selectedPositionData) {
        selectedPositionData = { top: '90%', left: '95%' };
    }

    // Evitar error si tamaño es undefined
    let tamanoActual;
    if (selectedIcon === 'accessibilidad-white' || selectedIcon === 'accessibilidad-black') {
        tamanoActual = tamanosCuadrados[valor];
    } else {
        tamanoActual = tamanosBoton[valor];
    }
    if (!tamanoActual) {
        // Fallback seguro
        tamanoActual = { width: '52px', height: '52px', fontSize: '16px' };
    }

    // Generar el script dinámicament


    // Función para enviar los valores al backend
    const saveWidgetValues = async () => {
        const color = colorPrincipal;
        const mode = modeMap[valor3] || "light";
        const iconPosition = iconPositionMap[selectedPosition] || "bottomRight";
        const autoDeploy = autoDeployMap[valor2] ?? false;
        const windowPosition = windowPositionMap[valor4] || "right";
        const iconType = iconTypeMap[selectedIcon] || "whiteRoundIcon";
        const defaultLanguage = valor5 || "es"; // Usar valor5 directamente
        const iconSizeValue = valor;

        const payload = {
            color,
            mode,
            iconPosition,
            autoDeploy,
            windowPosition,
            iconType,
            defaultLanguage,
            iconSizeValue,
            domain,
        };

        try {
            await api.post('/edit-widget-values', payload);
            Swal.fire({
                title: '¡Éxito!',
                text: '¡Tus cambios se han guardado correctamente!',
                icon: 'success',
                confirmButtonText: 'Aceptar',
                iconColor: 'green'
            });
        } catch (error) {
        }
    };

    // Valores por defecto para el widget
    const defaultWidgetValues = {
        colorPrincipal: '#0f50d2',
        selectedIcon: 'whiteRoundIcon',
        valor: 'Mediano',
        valor2: 'Cerrado',
        valor3: 'Claro',
        valor4: 'Derecha',
        valor5: 'Español',
        selectedPosition: 'bottom-right'
    };

    // Handler para cancelar y restaurar valores por defecto
    const handleCancel = () => {
        setColorPrincipal(defaultWidgetValues.colorPrincipal);
        setSelectedIcon(defaultWidgetValues.selectedIcon);
        setValor(defaultWidgetValues.valor);
        setValor2(defaultWidgetValues.valor2);
        setValor3(defaultWidgetValues.valor3);
        setValor4(defaultWidgetValues.valor4);
        setValor5(defaultWidgetValues.valor5);
        setSelectedPosition(defaultWidgetValues.selectedPosition);
    };

    // Nuevos estados para el modal de guías
    const [guidesModalOpen, setGuidesModalOpen] = useState(false);

    // Estados para el modal de añadir dominio
    const [modalOpen, setModalOpen] = useState(false);

    // // Funciones para el modal de guías
    // const handleOpenGuidesModal = () => {
    //     setGuidesModalOpen(true);
    // };

    const handleCloseGuidesModal = () => {
        setGuidesModalOpen(false);
    };

    // Función para añadir nuevo dominio
    const handleAddNewDomain = () => {
        setModalOpen(true);
    };

    // Funciones del modal de añadir dominio

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    return (
        <>
        <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100vw",
        px: { xs: 0, md: 2.5 },
        py: { xs: 0, md: 4 },
        pb: 0,
        mr: { xs: -6 },
        pr: { xs: 0 },
      }}
    >
      {/* Cabecera fija */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          padding: { xs: "12px 16px", sm: "8px 16px", md: "12px 16px" },
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          backgroundColor: "white",
          zIndex: 1000,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box sx={{ 
            display: { xs: "flex", sm: "flex" }, 
            flexDirection: "column", 
            flex: 1,
            ml: { xs: "50px", sm: "50px", md: 35.5 },
          }}>
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: "18px", sm: "18px", md: "20px", lg: "20px" },
                fontWeight: "medium",
                color: "#000",
              }}
            >
              Personalizar Widget <Box component="span" sx={{ color: '#0F52BD' }}>{domain}</Box>
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: { xs: '5px', sm: 2 },
              mr: { xs: 1.5, md: 5 },
              alignItems: "center",
            }}
          >
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontSize: '16px', 
                color: '#666',
                display: { xs: "none", lg: "block" },
                ml: { lg: 3 },
              }}
            >
              Desde este panel puedes configurar los aspectos relacionados con este dominio.
            </Typography>

            {/* Botón de añadir dominio - Solo icono en móvil */}
            <Box sx={{ display: { xs: "block", sm: "none" } }}>
              <IconButton
                onClick={handleAddNewDomain}
                sx={{
                  color: "#0F52BD",
                  backgroundColor: "transparent",
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#E3F2FD" },
                  width: 40,
                  height: 40,
                }}
              >
                <AddCircleOutlineIcon sx={{ fontSize: "28px" }} />
              </IconButton>
            </Box>

            {/* Botón de añadir dominio - Con texto en tablet/desktop */}
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddNewDomain}
                sx={{
                  textTransform: "none",
                  fontWeight: "medium",
                  backgroundColor: "#0F52BD",
                  fontSize: "14px",
                  px: 2,
                  py: 1,
                  borderRadius: "8px",
                }}
              >
                Añadir un nuevo dominio
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Spacer para empujar el contenido hacia abajo */}
      <Box
        sx={{
          height: { xs: "65px", sm: "70px", md: "100px", lg: "90px" },
          "@media (max-width: 600px) and (min-resolution: 1.25dppx)": {
            height: "65px",
          },
          "@media (max-width: 600px) and (min-resolution: 1.5dppx)": {
            height: "90px",
          },
          "@media (min-width: 600px) and (min-resolution: 1.25dppx)": {
            height: "75px",
          },
          "@media (min-width: 900px) and (min-resolution: 1.25dppx)": {
            height: "95px",
          },
        }}
      />

      <Box
        sx={{
          ml: { xs: -6, md: 0 },
          paddingLeft: { xs: 6, md: 2 },
          paddingRight: { xs: 0, md: 4 },
        }}
      >
            <Box sx={{ pb: 0, pt: { xs: 0, md: 2 } }}>
                {/* Remover el título "Personaliza tu widget" ya que está en el header */}
                
                {/* Mensaje Informativo */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: { xs: 4, md: 7 },
                        backgroundColor: '#E6F0FF',
                        borderRadius: 2,
                        paddingTop: '5px',
                        paddingBottom: '5px',
                        width: '100%',
                        px: 2
                    }}
                >
                    <InfoRoundedIcon sx={{ color: '#1976d2', mr: 1, flexShrink: 0 }} />
                    <Typography variant="body1" sx={{ color: '#0F52BD', fontSize: { xs: '14px', md: '16px' } }}>
                        Es conveniente configurar el widget en base a la identidad visual corporativa para garantizar una integración coherente con el sitio web.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: { xs: 4, lg: 5 } }}>
                    <Box sx={{ flex: { xs: 1, lg: 1.2 }, minWidth: 0 }}>
                        <Paper
                            sx={{
                                padding: { xs: 2, md: 3 },
                                position: "relative",
                                boxShadow: "0px 0px 24px 2px rgba(0, 0, 0, 0.2)",
                                width: '100%',
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                borderRadius: 3,
                                overflowX: 'hidden'
                            }}
                        >
                            <Typography sx={{ fontWeight: 'bold', fontSize: 20, mb: 2 }}>Color</Typography>

                            {/* 2. Pasar el color y el setter a PaletaColores */}
                            <PaletaColores color={colorPrincipal} setColor={setColorPrincipal} />

                            {/* Barra Separadora */}
                            <Box sx={{ display: 'flex', alignItems: 'center', my: 2, mt: 5 }}>
                                <Divider sx={{ flexGrow: 1, borderBottomWidth: 2, backgroundColor: '#b6b6b6' }} />
                            </Box>

                            <Typography sx={{ fontWeight: 'bold', fontSize: 20, mt: 4 }}>Botón de apertura</Typography>

                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' } }}>
                                <Box sx={{ mt: 2, flex: 1 }}>
                                    <Typography>Apariencia</Typography>

                                    <Box sx={{ mt: 2 }}>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                            {Object.entries(availableIcons)
                                                .filter(([key]) => key.includes("white"))
                                                .map(([key, url]) => (
                                                    /* Iconos seleccionables */
                                                    <Box
                                                        key={key}
                                                        component="img"
                                                        src={url}
                                                        alt={key}
                                                        onClick={() => setSelectedIcon(key)}
                                                        sx={{
                                                            width: 52,
                                                            height: 52,
                                                            borderRadius: '50%',
                                                            border: selectedIcon === key ? '2.5px solid #1976d2' : '2px solid white',
                                                            cursor: 'pointer',
                                                            boxShadow: selectedIcon === key ? '0 0 0 2px #1976d2' : 'none',
                                                            '&:hover': {
                                                                borderColor: '#1976d2',
                                                            },
                                                        }}
                                                    />
                                                ))}

                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                            {Object.entries(availableIcons)
                                                .filter(([key]) => key.includes("black"))
                                                .map(([key, url]) => (
                                                    <Box
                                                        key={key}
                                                        component="img"
                                                        src={url}
                                                        alt={key}
                                                        onClick={() => setSelectedIcon(key)}
                                                        sx={{
                                                            width: 52,
                                                            height: 52,
                                                            borderRadius: '50%',
                                                            border: selectedIcon === key ? '2.5px solid #1976d2' : '2px solid white',
                                                            cursor: 'pointer',
                                                            boxShadow: selectedIcon === key ? '0 0 0 2px #1976d2' : 'none',
                                                            '&:hover': {
                                                                borderColor: '#1976d2',
                                                            },
                                                        }}
                                                    />
                                                ))}
                                        </Box>
                                    </Box>
                                    <Typography sx={{ mt: 5.9 }}>Tamaño</Typography>

                                    <FormControl fullWidth sx={{ mt: 1 }}>
                                        <Select
                                            id="demo-simple-select"
                                            value={valor}
                                            onChange={handleChange}
                                            displayEmpty
                                            sx={{ fontSize: 15.5, height: 50 }}
                                        >
                                            <MenuItem value="Pequeño">Pequeño</MenuItem>
                                            <MenuItem value="Mediano">Mediano</MenuItem>
                                            <MenuItem value="Grande">Grande</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Barra vertical - solo visible en desktop */}
                                <Box sx={{ ml: { lg: 5 }, display: { xs: 'none', lg: 'block' } }}>
                                    <Divider orientation="vertical" sx={{ flexGrow: 1, borderRightWidth: 2, backgroundColor: '#b6b6b6' }} />
                                </Box>

                                <Box sx={{ ml: { xs: 0, lg: 5 }, mt: 2, flex: 1 }}>
                                    <Typography>Posición</Typography>
                                    {/* Pasa el estado y setter como props */}
                                    <SelectorPosicionador selectedPosition={selectedPosition} setSelectedPosition={setSelectedPosition} />
                                </Box>
                            </Box>

                            {/* Barra Separadora */}
                            <Box sx={{ display: 'flex', alignItems: 'center', my: 2, mt: 5 }}>
                                <Divider sx={{ flexGrow: 1, borderBottomWidth: 2, backgroundColor: '#b6b6b6' }} />
                            </Box>

                            <Typography sx={{ fontWeight: 'bold', fontSize: 20, mt: 4 }}>Mostrar por defecto</Typography>

                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' } }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ mt: 3 }}>Menú de accesibilidad</Typography>

                                    <FormControl fullWidth sx={{ mt: 1 }}>
                                        <Select
                                            id="demo-simple-select"
                                            value={valor2}
                                            onChange={handleChange2}
                                            displayEmpty
                                            sx={{ fontSize: 15.5, height: 50 }}
                                        >
                                            <MenuItem value="Abierto">Abierto</MenuItem>
                                            <MenuItem value="Cerrado">Cerrado</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <Typography sx={{ mt: 4 }}>Tema</Typography>

                                    <FormControl fullWidth sx={{ mt: 1 }}>
                                        <Select
                                            id="demo-simple-select"
                                            value={valor3}
                                            onChange={handleChange3}
                                            displayEmpty
                                            sx={{ fontSize: 15.5, height: 50 }}
                                        >
                                            <MenuItem value="Claro">
                                                <Box display="flex" alignItems="center">
                                                    <img
                                                        src={availableIcons.sol}
                                                        alt="Sol"
                                                        width={20}
                                                        height={20}
                                                        style={{ marginRight: 8 }}
                                                    />
                                                    <Typography fontSize={15.5}>Claro</Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="Oscuro">
                                                <Box display="flex" alignItems="center">
                                                    <img
                                                        src={availableIcons.luna}
                                                        alt="Luna"
                                                        width={20}
                                                        height={20}
                                                        style={{ marginRight: 8 }}
                                                    />
                                                    <Typography fontSize={15.5}>Oscuro</Typography>
                                                </Box>
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Barra vertical - solo visible en desktop */}
                                <Box sx={{ ml: { lg: 5.8 }, display: { xs: 'none', lg: 'block' } }}>
                                    <Divider orientation="vertical" sx={{ flexGrow: 1, borderRightWidth: 2, backgroundColor: '#b6b6b6' }} />
                                </Box>

                                <Box sx={{ mt: 3, ml: { xs: 0, lg: 5 }, flex: 1 }}>
                                    <Typography>Posición de apertura</Typography>

                                    <FormControl fullWidth sx={{ mt: 1 }}>
                                        <Select
                                            id="demo-simple-select"
                                            value={valor4}
                                            onChange={handleChange4}
                                            displayEmpty
                                            sx={{ fontSize: 15.5, height: 50 }}
                                        >
                                            <MenuItem value="Derecha">Derecha</MenuItem>
                                            <MenuItem value="Izquierda">Izquierda</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <Typography sx={{ mt: 4 }}>Idioma</Typography>

                                    <FormControl fullWidth sx={{ mt: 1 }}>
                                        <Select
                                            id="demo-simple-select"
                                            value={valor5}
                                            onChange={handleChange5}
                                            displayEmpty
                                            sx={{ fontSize: 15.5, height: 50 }}
                                        >
                                            <MenuItem value="es">Español (España)</MenuItem>
                                            <MenuItem value="en">Inglés (Reino Unido)</MenuItem>
                                            <MenuItem value="bg">Búlgaro</MenuItem>
                                            <MenuItem value="fr">Francés</MenuItem>
                                            <MenuItem value="de">Alemán</MenuItem>
                                            <MenuItem value="ca">Catalán</MenuItem>
                                            <MenuItem value="eu">Euskera</MenuItem>
                                            <MenuItem value="gl">Gallego</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ flex: { xs: 1, lg: 0.8 }, minWidth: 0 }}>
                        <Paper
                            sx={{
                                padding: { xs: 2, md: 3 },
                                position: "relative",
                                boxShadow: "0px 0px 24px 2px rgba(0, 0, 0, 0.2)",
                                width: '100%',
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                borderRadius: 3,
                                overflow: 'visible',
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0
                            }}
                        >
                            <Typography sx={{ fontWeight: 'bold', fontSize: 20, mb: 2 }}>Previsualización</Typography>

                            {/* Container for both previews with equal flex */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 4 }}>
                                
                                {/* First preview box */}
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxWidth: { xs: '100%', md: '600px' },
                                        bgcolor: 'white',
                                        borderRadius: 2,
                                        boxShadow: 3,
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        mx: 'auto',
                                        overflow: 'hidden',
                                        minHeight: { xs: 250, md: 350 }, // Fixed minimum height
                                        flex: 1 // Equal flex distribution
                                    }}>

                                    {/* Widget menu - make it proportional */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: valor4 === 'Derecha' ? 0 : 'auto',
                                            left: valor4 === 'Izquierda' ? 0 : 'auto',
                                            zIndex: 10,
                                            maxWidth: '40%'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: { xs: 120, sm: 150, md: 200 },
                                                height: { xs: 240, sm: 300, md: 406 },
                                                bgcolor: 'white',
                                                borderRadius: 2.1,
                                                boxShadow: 3,
                                                overflow: 'hidden',
                                                position: 'relative',
                                                backgroundColor: valor3 === 'Oscuro' ? 'black' : 'white',
                                            }}
                                        >
                                            <Box sx={{ height: { xs: 12, sm: 15, md: 20 }, bgcolor: colorPrincipal }} />
                                            
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                                <Box
                                                    sx={{
                                                        width: '60%',
                                                        height: { xs: 12, sm: 14, md: 16 },
                                                        bgcolor: '#d3d3d3',
                                                        borderRadius: 1,
                                                }}
                                                />
                                            </Box>

                                            {[0, 1, 2].map((section) => (
                                                <Box key={section}>
                                                    <Box
                                                        sx={{
                                                            height: { xs: 1, sm: 1.5, md: 2 },
                                                            bgcolor: colorPrincipal,
                                                            my: { xs: 1, sm: 1.5, md: 2 },
                                                            mx: 1,
                                                            borderRadius: 1,
                                                        }}
                                                    />
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', px: 1, gap: { xs: 0.5, md: 1 } }}>
                                                        {[...Array(6)].map((_, i) => (
                                                            <Box
                                                                key={i}
                                                                sx={{
                                                                    width: '30%',
                                                                    height: { xs: 20, sm: 25, md: 33.4 },
                                                                    bgcolor: '#d3d3d3',
                                                                    borderRadius: 1,
                                                                    flexGrow: 1,
                                                                    flexBasis: '30%',
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            ))}

                                            <Box sx={{ height: { xs: 12, sm: 15, md: 20 }, bgcolor: colorPrincipal, mt: 2 }} />
                                        </Box>
                                    </Box>

                                    {/* Header */}
                                    <Box
                                        sx={{
                                            height: 40,
                                            bgcolor: '#c7c7c7',
                                            borderTopLeftRadius: 9,
                                            borderTopRightRadius: 9,
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%'
                                        }}
                                    >
                                        <Box sx={{ width: 12, height: 12, backgroundColor: 'white', borderRadius: '50%', ml: 1.8 }} />
                                        <Box
                                            sx={{
                                                height: 12,
                                                width: '85%',
                                                borderRadius: 8,
                                                backgroundColor: 'white',
                                                opacity: 0.9,
                                                margin: '0 auto',
                                            }}
                                        />
                                        <Box sx={{ width: 12, height: 12, backgroundColor: 'white', borderRadius: '50%', mr: 1.8 }} />
                                    </Box>

                                    {/* Content with flex: 1 to fill remaining space */}
                                    <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                            <Box
                                                sx={{
                                                    backgroundColor: "#0f50d2",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    padding: "4px",
                                                    borderRadius: "20px",
                                                    width: { xs: '90%', md: '70%' },
                                                    gap: { xs: "8px", md: "16px" },
                                                    justifyContent: 'center',
                                                    flexWrap: 'wrap'
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: { xs: "50px", md: "70px" },
                                                        height: { xs: "20px", md: "30px" },
                                                        borderRadius: "15px",
                                                        backgroundColor: "white",
                                                        my: 0.5
                                                    }}
                                                />
                                                {[...Array(3)].map((_, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            width: { xs: "40px", md: "60px" },
                                                            height: { xs: "16px", md: "24px" },
                                                            borderRadius: "12px",
                                                            backgroundColor: "#bbcaf0",
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={{
                                                width: { xs: "40px", md: "60px" },
                                                height: { xs: "16px", md: "24px" },
                                                borderRadius: "12px",
                                                backgroundColor: "#bbcaf0",
                                                mt: { xs: 3, md: 5 },
                                                ml: { xs: 2, md: 3 }
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                backgroundColor: "#0f50d2",
                                                padding: "4px",
                                                borderRadius: "20px",
                                                width: { xs: "60%", md: "40%" },
                                                height: { xs: 30, md: 40 },
                                                ml: { xs: 2, md: 3 },
                                                mt: 1
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                backgroundColor: "#dddddd",
                                                padding: "4px",
                                                borderRadius: "15px",
                                                width: { xs: "80%", md: "55%" },
                                                height: { xs: 60, md: 80 },
                                                ml: { xs: 2, md: 3 },
                                                mt: 1
                                            }}
                                        />

                                        <Box sx={{ display: 'flex', ml: { xs: 2, md: 3 }, mt: 1, gap: 2 }}>
                                            <Box
                                                sx={{
                                                    width: { xs: "40px", md: "60px" },
                                                    height: { xs: "16px", md: "24px" },
                                                    borderRadius: "12px",
                                                    backgroundColor: "#0f50d2",
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    width: { xs: "40px", md: "60px" },
                                                    height: { xs: "16px", md: "24px" },
                                                    borderRadius: "12px",
                                                    backgroundColor: "white",
                                                    border: '2px solid #0f50d2',
                                                }}
                                            />
                                        </Box>

                                        {/* Spacer to push the text to bottom */}
                                        <Box sx={{ flex: 1 }} />
                                        
                                        <Typography sx={{ ml: { xs: 2, md: 3 }, mb: 2, color: '#3e3e3e', fontSize: { xs: '12px', md: '14px' } }}>
                                            Color
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Divider */}
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Divider sx={{ flexGrow: 1, borderBottomWidth: 2, backgroundColor: '#b6b6b6' }} />
                                </Box>

                                {/* Second preview box - exactly same structure as first */}
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxWidth: { xs: '100%', md: '600px' },
                                        bgcolor: 'white',
                                        borderRadius: 2,
                                        boxShadow: 3,
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        mx: 'auto',
                                        overflow: 'hidden',
                                        minHeight: { xs: 250, md: 350 }, // Same fixed minimum height
                                        flex: 1 // Equal flex distribution
                                    }}>
                                    
                                    {/* Widget button positioning */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            zIndex: 10,
                                            top: selectedPositionData.top,
                                            left: selectedPositionData.left,
                                            transform: 'translate(-50%, -50%)',
                                        }}
                                    >
                                        {/* Widget icon rendering */}
                                        {selectedIcon === 'accessibilidad-white' ? (
                                            <Box
                                                variant="outlined"
                                                sx={{
                                                    border: '2px solid black',
                                                    borderRadius: '8px',
                                                    width: tamanoActual.width,
                                                    height: tamanoActual.height,
                                                    px: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all',
                                                    maxWidth: '120px',
                                                    fontSize: { xs: '10px', md: tamanoActual.fontSize }
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        color: 'black',
                                                        fontWeight: 'bold',
                                                        fontSize: { xs: '8px', sm: '10px', md: tamanoActual.fontSize }
                                                    }}
                                                >
                                                    Accessibilidad
                                                </Typography>
                                            </Box>
                                        ) : selectedIcon === 'accessibilidad-black' ? (
                                            <Box
                                                sx={{
                                                    display: 'inline-block',
                                                    padding: '2px',
                                                    borderRadius: '8px',
                                                    backgroundColor: 'black',
                                                    transition: 'all',
                                                    maxWidth: '120px'
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        backgroundColor: 'black',
                                                        border: '2px solid white',
                                                        borderRadius: '6px',
                                                        width: tamanoActual.width,
                                                        height: tamanoActual.height,
                                                        px: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        transition: 'all'
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            fontSize: { xs: '8px', sm: '10px', md: tamanoActual.fontSize }
                                                        }}
                                                    >
                                                        Accessibilidad
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: tamanoActual.width,
                                                    height: tamanoActual.height,
                                                    backgroundImage: `url(${availableIcons[selectedIcon]})`,
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center',
                                                    backgroundRepeat: 'no-repeat',
                                                    transition: 'all',
                                                    maxWidth: '80px',
                                                    maxHeight: '80px'
                                                }}
                                            />
                                        )}
                                    </Box>

                                    {/* Header - same as first */}
                                    <Box
                                        sx={{
                                            height: 40,
                                            bgcolor: '#c7c7c7',
                                            borderTopLeftRadius: 9,
                                            borderTopRightRadius: 9,
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%'
                                        }}
                                    >
                                        <Box sx={{ width: 12, height: 12, backgroundColor: 'white', borderRadius: '50%', ml: 1.8 }} />
                                        <Box
                                            sx={{
                                                height: 12,
                                                width: '85%',
                                                borderRadius: 8,
                                                backgroundColor: 'white',
                                                opacity: 0.9,
                                                margin: '0 auto',
                                            }}
                                        />
                                        <Box sx={{ width: 12, height: 12, backgroundColor: 'white', borderRadius: '50%', mr: 1.8 }} />
                                    </Box>

                                    {/* Content - same structure as first with flex: 1 */}
                                    <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                            <Box
                                                sx={{
                                                    backgroundColor: "#0f50d2",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    padding: "4px",
                                                    borderRadius: "20px",
                                                    width: { xs: '90%', md: '70%' },
                                                    gap: { xs: "8px", md: "16px" },
                                                    justifyContent: 'center',
                                                    flexWrap: 'wrap'
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: { xs: "50px", md: "70px" },
                                                        height: { xs: "20px", md: "30px" },
                                                        borderRadius: "15px",
                                                        backgroundColor: "white",
                                                        my: 0.5
                                                    }}
                                                />
                                                {[...Array(3)].map((_, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            width: { xs: "40px", md: "60px" },
                                                            height: { xs: "16px", md: "24px" },
                                                            borderRadius: "12px",
                                                            backgroundColor: "#bbcaf0",
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={{
                                                width: { xs: "40px", md: "60px" },
                                                height: { xs: "16px", md: "24px" },
                                                borderRadius: "12px",
                                                backgroundColor: "#bbcaf0",
                                                mt: { xs: 3, md: 5 },
                                                ml: { xs: 2, md: 3 }
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                backgroundColor: "#0f50d2",
                                                padding: "4px",
                                                borderRadius: "20px",
                                                width: { xs: "60%", md: "40%" },
                                                height: { xs: 30, md: 40 },
                                                ml: { xs: 2, md: 3 },
                                                mt: 1
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                backgroundColor: "#dddddd",
                                                padding: "4px",
                                                borderRadius: "15px",
                                                width: { xs: "80%", md: "55%" },
                                                height: { xs: 60, md: 80 },
                                                ml: { xs: 2, md: 3 },
                                                mt: 1
                                            }}
                                        />

                                        <Box sx={{ display: 'flex', ml: { xs: 2, md: 3 }, mt: 1, gap: 2 }}>
                                            <Box
                                                sx={{
                                                    width: { xs: "40px", md: "60px" },
                                                    height: { xs: "16px", md: "24px" },
                                                    borderRadius: "12px",
                                                    backgroundColor: "#0f50d2",
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    width: { xs: "40px", md: "60px" },
                                                    height: { xs: "16px", md: "24px" },
                                                    borderRadius: "12px",
                                                    backgroundColor: "white",
                                                    border: '2px solid #0f50d2',
                                                }}
                                            />
                                        </Box>

                                        {/* Spacer to push the text to bottom - same as first */}
                                        <Box sx={{ flex: 1 }} />
                                        
                                        <Typography sx={{ ml: { xs: 2, md: 3 }, mb: 2, color: '#3e3e3e', fontSize: { xs: '12px', md: '14px' } }}>
                                            Apariencia, tamaño y posición
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Buttons */}
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'flex-end', 
                                gap: { xs: 2, md: 4 }, 
                                mt: { xs: 4, md: 6 },
                                flexDirection: { xs: 'column', sm: 'row' },
                                width: '100%'
                            }}>
                                <Button
                                    sx={{
                                        color: '#0f50d2',
                                        fontSize: '15px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        px: 3,
                                        py: 1,
                                        borderRadius: '8px',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            backgroundColor: '#ededed',
                                            boxShadow: 'none',
                                        },
                                    }}
                                    onClick={handleCancel}
                                >
                                    Restablecer
                                </Button>

                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor: '#0f50d2',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '15px',
                                        borderRadius: '8px',
                                        px: 3,
                                        py: 1,
                                        boxShadow: 'none',
                                        '&:hover': {
                                            backgroundColor: '#0c42b5',
                                            boxShadow: 'none',
                                        },
                                    }}
                                    onClick={saveWidgetValues}
                                >
                                    Guardar cambios
                                </Button>
                            </Box>
                        </Paper>
                    </Box>
                </Box>

                
            </Box>
      </Box>
    </Box>

            {/* Modal de guías - corregido como en Home.jsx */}
            <Dialog 
                open={guidesModalOpen} 
                onClose={handleCloseGuidesModal} 
                fullWidth 
                maxWidth="lg"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 0,
                        m: 0,
                        overflow: 'hidden',
                        '& .MuiDialogContent-root': {
                            padding: 0,
                            margin: 0,
                        }
                    }
                }}
            >
                <DialogContent sx={{ 
                    padding: 0, 
                    margin: 0,
                    '&:first-of-type': {
                        paddingTop: 0
                    }
                }}>
                    <PlataformaSelector onClose={handleCloseGuidesModal} />
                </DialogContent>
            </Dialog>

            {/* Modal para añadir nuevo dominio - como en Home.jsx */}
            <Dialog open={modalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 'medium', fontSize: '24px' }}>{t("Suscribirse a un Widget")}</DialogTitle>
                <DialogContent sx={{ padding: '25px' }}>
                    <ActiveCampaignForm open={modalOpen} />
                </DialogContent>
                <DialogActions sx={{ paddingRight: '25px', paddingBottom: '25px' }}>
                    <Button onClick={handleCloseModal} sx={{ textTransform: 'none', fontSize: '16px', fontWeight: 'regular' }}>
                        {t("Cerrar")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PersonalizarWidget;
